import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/design/design/node_modules/@primer/gatsby-theme-doctocat/src/components/layout.js";
import { Box } from '@primer/react';
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const DoDontContainer = makeShortcode("DoDontContainer");
const Do = makeShortcode("Do");
const Caption = makeShortcode("Caption");
const Dont = makeShortcode("Dont");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <p>{`Primer includes three different messaging components:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://primer.style/css/components/toasts"
        }}>{`Toasts`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://primer.style/css/components/alerts"
        }}>{`Flash alerts`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://primer.style/css/components/popover"
        }}>{`Popovers`}</a></li>
    </ul>
    <h2>{`Messaging types`}</h2>
    <p>{`It's important to consider the context when selecting a component to convey your message. Does the message need to interrupt the user flow? Is this a global message that needs to be shown on every page? Does it require the user to take an action, or is it more of a notice? Most messaging on GitHub falls into one of the following categories:`}</p>
    <p><strong parentName="p">{`Feedback`}</strong>{`: communicates something to the user based on an action they've taken or a change in context`}</p>
    <ul>
      <li parentName="ul"><em parentName="li">{`Toasts or flash alerts are best suited for this type of messaging.`}</em></li>
    </ul>
    <p><strong parentName="p">{`Highlights`}</strong>{`: bring attention to how and why to engage with a feature`}</p>
    <ul>
      <li parentName="ul"><em parentName="li">{`Popovers are best suited for this type of messaging.`}</em></li>
    </ul>
    <p><strong parentName="p">{`Updates`}</strong>{`: inform the user of necessary updates or changes to the product as it relates to them.`}</p>
    <ul>
      <li parentName="ul"><em parentName="li">{`Flash alerts are best suited for this type of messaging.`}</em></li>
    </ul>
    <h2>{`Toasts`}</h2>
    <Box mb="3" mt="4" mdxType="Box">
  <img width="453" src="https://user-images.githubusercontent.com/586552/63105014-f5477a00-bf4d-11e9-954e-f01582a39ef3.png" />
    </Box>
    <p><a parentName="p" {...{
        "href": "https://primer.style/css/components/toasts"
      }}>{`Toasts`}</a>{` are used to provide relevant feedback to the user, typically after they've taken an action. Primer includes styles for success, warning, error, loading, and default toasts. Some examples include:`}</p>
    <ul>
      <li parentName="ul">{`Confirmation that an action was successfully taken`}</li>
      <li parentName="ul">{`Communicating that an action is pending`}</li>
      <li parentName="ul">{`Notifying the user if an action failed to complete`}</li>
      <li parentName="ul">{`Providing general information`}</li>
    </ul>
    <p>{`Toasts are best used in context of the page they're referring to (rather than a global notification that can appear on any page) and should require minimal user interaction. Toasts are best used to display time-sensitive information. For global notices and messaging, see the `}<a parentName="p" {...{
        "href": "#flash-alerts"
      }}>{`flash component`}</a>{`.`}</p>
    <p>{`Toasts should be brief and not bog down the experience with superfluous copy. If multiple toasts appear on the page, they will stack naturally.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img src="https://user-images.githubusercontent.com/586552/63106528-06de5100-bf51-11e9-8a5e-98583ed74874.png" />
    <Caption mdxType="Caption">Use brief and direct communication</Caption>
  </Do>
  <Dont mdxType="Dont">
    <img src="https://user-images.githubusercontent.com/586552/63106527-06de5100-bf51-11e9-858c-72de6a5c728a.png" />
    <Caption mdxType="Caption">Don't use wordy and redundant copy</Caption>
  </Dont>
    </DoDontContainer>
    <h3>{`Accessibility`}</h3>
    <p>{`Toasts are non-modal components and should contain `}<inlineCode parentName="p">{`role=log`}</inlineCode>{`, which implies the element has `}<inlineCode parentName="p">{`aria-live="polite"`}</inlineCode>{`. This notifies the user of the toast via Assistance Technologies without having to change focus to the toast. You can read more about `}<inlineCode parentName="p">{`role=log`}</inlineCode>{` at `}<a parentName="p" {...{
        "href": "https://www.w3.org/WAI/WCAG21/Techniques/aria/ARIA23"
      }}>{`W3: Using `}<inlineCode parentName="a">{`role=log`}</inlineCode>{` to identify sequential information updates`}</a>{`.`}</p>
    <p>{`Toasts are generally informative and should not receive focus when they appear. For that reason, we suggest you `}<strong parentName="p">{`avoid using interactive elements`}</strong>{` in the component (aside from a dismiss action). Keep in mind that, even without an explicit dismiss action, the user can always hit `}<inlineCode parentName="p">{`esc`}</inlineCode>{` to dismiss the toast. For more information on how interactive children affect web accessibility, `}<a parentName="p" {...{
        "href": "https://github.com/jackbsteinberg/std-toast/issues/29"
      }}>{`check out this issue`}</a>{`.`}</p>
    <h2>{`Flash alerts`}</h2>
    <Box mb="2" mt="4" mdxType="Box">
  <img src="https://user-images.githubusercontent.com/586552/63052321-055b4d00-bead-11e9-95ba-1d4cdfb34dae.png" />
    </Box>
Flash alerts are used to display updates or alerts pertaining to any part of the system; this includes information regarding
the user's account, the organization, the repo, and more. These updates are typically not user-initiated but rather alerts
that require the user's attention. Similarly to toasts, flash alerts can convey a warning, error, success, or a neutral message.
    <p>{`Though flash alerts don't need to be as concise as a typical Toast message, they should still be direct and not exceed two to three sentences. If additional context is needed, provide a link for the user to learn more.`}</p>
    <p>{`Don't use headings or multiple type sizes in flash alerts, rely on our default paragraph size instead.`}</p>
    <h3>{`Full-width`}</h3>
    <p>{`Flash alerts that affect every page (e.g. a global message) should use the `}<a parentName="p" {...{
        "href": "https://primer.style/css/components/alerts"
      }}>{`full-width style`}</a>{`, which spans the entire page and is intended to sit beneath the top-level navigation. When using a full-width flash alert, be sure to include the `}<inlineCode parentName="p">{`container`}</inlineCode>{` class to append a max-width on the content and avoid long line-lengths, which can be difficult to read.`}</p>
    <DoDontContainer stacked mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img src="https://user-images.githubusercontent.com/586552/63046880-46e5fb00-bea1-11e9-836d-be1b1c7d963f.png" />
    <Caption mdxType="Caption">Use a container class to give the text a max-width</Caption>
  </Do>
  <Dont mdxType="Dont">
    <img src="https://user-images.githubusercontent.com/586552/63046881-46e5fb00-bea1-11e9-87ee-80dbeb0bea1c.png" />
    <Caption mdxType="Caption">
      Don’t let a single text block span the entire width of the window (non-text elements can still span the entire
      width, such as left-aligned text and right-aligned buttons)
    </Caption>
  </Dont>
    </DoDontContainer>
    <h3>{`Interactive elements`}</h3>
    <p>{`Flash alerts can contain links or buttons depending on the type CTA and how much attention it should draw.`}</p>
    <p>{`They typically do not timeout on their own, but can include a dismiss action for messages that can be closed or hidden.`}</p>
    <h2>{`Popovers`}</h2>
    <Box display="flex" flexDirection={['column', 'row', 'row']} mdxType="Box">
  <Box mr="3" mb="2" mdxType="Box">
    <img width="262" src="https://user-images.githubusercontent.com/586552/63109550-8111d400-bf57-11e9-8714-46607da2f2ba.png" />
  </Box>
  <Box mb="2" mdxType="Box">
    <img width="386" src="https://user-images.githubusercontent.com/586552/63109551-8111d400-bf57-11e9-893b-2cdeffffd147.png" />
  </Box>
    </Box>
    <p>{`Popovers are used to call attention to a new feature, change to an existing feature, or to provide additional context. Popovers can be helpful for flows that require light onboarding or instruction.`}</p>
    <p><strong parentName="p">{`Use sparingly to avoid cognitive overload`}</strong>{`. Though they can be used for a variety of things, they should be used sparingly to avoid cognitive overload. It's important to consider the context in which the popover appears. Are there other popovers on the page? Does it appear on page load, or require the user to open the popover?`}</p>
    <p>{`Unlike other messaging components, popovers should never include critical information (such as errors) and should always include a dismiss action.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      